import * as React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Select } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import { Stack } from "components/layout/reusableLayouts";
import { contactName } from "utils/fullName";
import Summary, { SummaryItem } from "components/common/Summary";
import moment from "moment";
import { formatCashFull } from "utils/formatCash";
import { toast } from "react-toastify";
import CurrencyInput from "components/admin/common/CurrencyInput";

/** What this dialog reads off an invoice, pending a typed Invoice. */
export interface RefundableInvoice {
  id?: string;
  currency?: string;
  refundTransactions?: { amount: number; transactionDate: string }[];
  primaryRecipient?: {
    contactType?: string;
    businessName?: string;
    firstName?: string;
    lastName?: string | null;
  };
}

export interface RefundRequest {
  amount: number;
  reason: string;
}

interface RefundModalProps {
  refundTitle: string;
  description: React.ReactNode;
  open: boolean;
  handleYes: (data: RefundRequest) => void;
  onCloseModal: () => void;
  isLoading: boolean;
  /** What the server would still refund; nothing can be refunded until it is known. */
  maxRefund?: number;
  invoice?: RefundableInvoice;
}

const REFUND_REASON_IDS = [
  "other",
  "duplicate",
  "fraudulent",
  "requested_by_customer",
] as const;

const RefundModal: React.FC<RefundModalProps> = ({
  refundTitle,
  description,
  open,
  handleYes,
  onCloseModal,
  isLoading = false,
  maxRefund,
  invoice,
}) => {
  const { t } = useTranslation();

  // NaN until there is a limit to start from, which the money field shows as blank.
  const [amount, setAmount] = React.useState(NaN);
  const [reason, setReason] = React.useState<string>(REFUND_REASON_IDS[0]);

  const reasonOptions = React.useMemo(
    () =>
      REFUND_REASON_IDS.map((id) => ({ id, label: t(`refundFeature.${id}`) })),
    [t],
  );

  const amountError =
    maxRefund !== undefined && amount > Number(maxRefund.toFixed(2))
      ? `${t("refundFeature.maxRefundError")} ${formatCashFull(maxRefund, invoice?.currency)}`
      : undefined;

  // A blank or zero amount is not an error to show while typing, but it is not a refund either.
  const submitError =
    amountError ??
    (amount > 0 ? undefined : t("Amount must be greater than 0"));

  // Both call sites keep this mounted between refunds, so every open starts
  // over, and a zero limit is a limit rather than a reason to keep the old amount.
  React.useEffect(() => {
    if (!open) return;
    setAmount(maxRefund ?? NaN);
    setReason(REFUND_REASON_IDS[0]);
  }, [open, invoice?.id, maxRefund]);

  const summaryItems: SummaryItem[] = React.useMemo(() => {
    const refundItems: SummaryItem[] = (invoice?.refundTransactions ?? []).map(
      (item) => ({
        title: t("Refunded"),
        value: `${formatCashFull(item.amount, invoice?.currency)} ${moment(item.transactionDate).format("MM/DD/YYYY")}`,
        fullWidth: true,
      }),
    );
    const recipient = invoice?.primaryRecipient;
    return [
      {
        title: t("Recipient"),
        value: contactName(recipient) || null,
      },
      ...refundItems,
    ];
  }, [invoice, t]);

  return (
    <Dialog
      open={open}
      // A refund in flight cannot be dismissed: closing would read as cancelled
      // while the mutation runs on.
      onClose={() => {
        if (isLoading) return;
        onCloseModal();
      }}
      maxWidth="xs"
      fullWidth
      aria-labelledby="refund-dialog-title"
    >
      <DialogTitle id="refund-dialog-title">{refundTitle}</DialogTitle>
      <DialogContent>
        <Stack gap="var(--space-5)">
          <Typography
            variant="body2"
            sx={{ color: "var(--color-text-secondary)" }}
          >
            {description}
          </Typography>
          <CurrencyInput
            id="refund-amount"
            required
            label={t("Amount")}
            placeholder={t("refundFeature.amountToRefund")}
            name="amount"
            value={amount}
            currency={invoice?.currency}
            // The money field ignores new values while focused, so typing before the limit lands would go unseen.
            disabled={maxRefund === undefined}
            // errorText supersedes helperText, and the DS resolves the pair as
            // `errorText ?? helperText`, so the absent case has to be undefined.
            errorText={amountError}
            helperText={
              maxRefund === undefined
                ? undefined
                : `${t("refundFeature.maxRefundError")} ${formatCashFull(maxRefund, invoice?.currency)}`
            }
            onChange={setAmount}
          />
          <Select
            label={t("refundFeature.reasonForRefund")}
            options={reasonOptions}
            value={reason}
            onChange={(value) => setReason(value ?? REFUND_REASON_IDS[0])}
          />
          <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
            <Summary items={summaryItems} />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          variant="default-outlined"
          disabled={isLoading}
          onClick={onCloseModal}
        >
          {t("No")}
        </Button>
        <Button
          variant="primary-filled"
          disabled={isLoading || maxRefund === undefined}
          onClick={() => {
            if (submitError) {
              toast.error(submitError);
              return;
            }
            handleYes({ amount, reason });
          }}
        >
          {isLoading ? t("Refunding") : t("Yes")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RefundModal;
